import { navigate } from 'gatsby';

import {
  ALLERGIES,
  APPOINTMENT_HISTORY,
  BODY,
  EMERGENCY_VISITS,
  FAMILY_HISTORY,
  GENETIC_INFORMATION,
  HOSPITALIZATIONS,
  IMAGING_TESTS,
  IMMUNIZATIONS,
  INSURANCE,
  LAB_RESULTS,
  MEDICAL_CONDITIONS,
  MEDICATIONS,
  SURGERIES,
  VITAL_SIGNS,
} from '@constants/routes';

import { useDeleteAllergy } from '@hooks/api/allergy';
import { useDeleteAppointment } from '@hooks/api/appointment';
import { useDeleteBodyObservation } from '@hooks/api/body-observation';
import {
  useDeleteLabDiagnosticReport,
  useDeleteImagingDiagnosticReport,
} from '@hooks/api/diagnostic-report';
import { useDeleteEmergencyVisit } from '@hooks/api/emergency-visit';
import { useDeleteFamilyHistory } from '@hooks/api/family-history';
import { useDeleteGeneticInformation } from '@hooks/api/genetic-information';
import { useDeleteHospitalization } from '@hooks/api/hospitalization';
import { useDeleteImmunization } from '@hooks/api/immunization';
import { useDeleteInsurance } from '@hooks/api/insurance';
import { useDeleteMedicalCondition } from '@hooks/api/medical-condition';
import { useDeleteMedicationStatement } from '@hooks/api/medication';
import { useDeleteProcedure } from '@hooks/api/procedure';
import { useDeleteVitalSign } from '@hooks/api/vital-sign';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';
import type { EncounterRecord } from '@models/encounter';
import type { FhirResource } from '@models/fhir-resource';
import type { ObservationRecord } from '@models/observation';

import type { OperationOutcomeModel } from 'generated/types';

export function useDeleteRecord() {
  const [deleteAppointment] = useDeleteAppointment();
  const [deleteAllergy] = useDeleteAllergy();
  const [deleteBodyObservation] = useDeleteBodyObservation();
  const [deleteCondition] = useDeleteMedicalCondition();
  const [deleteImagingDiagnosticReport] = useDeleteImagingDiagnosticReport();
  const [deleteLabDiagnosticReport] = useDeleteLabDiagnosticReport();
  const [deleteEmergencyVisit] = useDeleteEmergencyVisit();
  const [deleteFamilyHistory] = useDeleteFamilyHistory();
  const [deleteGeneticInfo] = useDeleteGeneticInformation();
  const [deleteHospitalization] = useDeleteHospitalization();
  const [deleteImmunization] = useDeleteImmunization();
  const [deleteInsurance] = useDeleteInsurance();
  const [deleteMedication] = useDeleteMedicationStatement();
  const [deleteProcedure] = useDeleteProcedure();
  const [deleteVitalSign] = useDeleteVitalSign();

  async function deleteRecord(
    record: FhirResource
  ): Promise<OperationOutcomeModel | undefined> {
    const { id, resourceType } = record;
    if (!id) {
      throw new Error('No record selected for deletion');
    }
    if (resourceType === 'Appointment') {
      const { data } = await deleteAppointment({
        input: {
          appointment_id: id,
        },
      });
      if (data?.deleteAppointmentRecord.resourceType) {
        navigate(APPOINTMENT_HISTORY);
      }
      return data?.deleteAppointmentRecord;
    } else if (resourceType === 'DiagnosticReport') {
      const { category } = record as DiagnosticReportRecord;
      if (category === 'laboratory') {
        const { data } = await deleteLabDiagnosticReport({
          input: {
            diagnostic_report_id: id,
          },
        });
        if (data?.deleteLabReport.resourceType) {
          navigate(LAB_RESULTS);
        }
        return data?.deleteLabReport;
      } else {
        const { data } = await deleteImagingDiagnosticReport({
          input: {
            diagnostic_report_id: id,
          },
        });
        if (data?.deleteImagingTest.resourceType) {
          navigate(IMAGING_TESTS);
        }
        return data?.deleteImagingTest;
      }
    } else if (resourceType === 'Observation') {
      const { category } = record as ObservationRecord;
      if (category === 'genetic-information') {
        const { data } = await deleteGeneticInfo({
          input: { observation_id: id },
        });
        if (data?.deleteGeneticObservationRecord.resourceType) {
          navigate(GENETIC_INFORMATION);
        }
        return data?.deleteGeneticObservationRecord;
      } else if (category === 'vital-signs') {
        const { data } = await deleteVitalSign({
          input: { observation_id: id },
        });
        if (data?.deleteVitalSignObservationRecord.resourceType) {
          navigate(VITAL_SIGNS);
        }
        return data?.deleteVitalSignObservationRecord;
      } else if (category === 'body') {
        const { data } = await deleteBodyObservation({
          input: { observation_id: id },
        });
        if (data?.deleteBodyObservationRecord.resourceType) {
          navigate(BODY);
        }
        return data?.deleteBodyObservationRecord;
      }
      /*
      const { data } = await deleteObservation({
        variables: {
          input: { observation_id: id },
        },
        update: (cache) => {
          const normalizedId = cache.identify({
            id,
            __typename: 'ObservationModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        },
      });
      // records/body/body-weight/123 -> records/body/body-weight
      const pathParts = location.pathname.split('/');
      const newPath = pathParts.slice(0, pathParts.length - 1).join('/');
      navigate(newPath);
      return data?.deleteObservationRecord;
      */
    } else if (resourceType === 'Coverage') {
      const { data } = await deleteInsurance({ input: { coverage_id: id } });
      if (data?.deleteCoverageRecord.resourceType) {
        navigate(INSURANCE);
      }
      return data?.deleteCoverageRecord;
    } else if (resourceType === 'FamilyMemberHistory') {
      const { data } = await deleteFamilyHistory({
        input: {
          familymemberhistory_id: id,
        },
      });
      if (data?.deleteFamilyMemberHistoryRecord.resourceType) {
        navigate(FAMILY_HISTORY);
      }
      return data?.deleteFamilyMemberHistoryRecord;
    } else if (resourceType === 'Immunization') {
      const { data } = await deleteImmunization({
        input: { immunization_id: id },
      });
      if (data?.deleteImmunizationRecord.resourceType) {
        navigate(IMMUNIZATIONS);
      }
      return data?.deleteImmunizationRecord;
    } else if (resourceType === 'AllergyIntolerance') {
      const { data } = await deleteAllergy({
        input: {
          allergy_id: id,
        },
      });
      if (data?.deleteAllergyRecord.resourceType) {
        navigate(ALLERGIES);
      }
      return data?.deleteAllergyRecord;
    } else if (resourceType === 'MedicationStatement') {
      const { data } = await deleteMedication({
        input: { medication_statement_id: id },
      });
      if (data?.deleteMedicationStatementRecord.resourceType) {
        navigate(MEDICATIONS);
      }
      return data?.deleteMedicationStatementRecord;
    } else if (resourceType === 'Condition') {
      const { data } = await deleteCondition({
        input: {
          condition_id: id,
        },
      });
      if (data?.deleteConditionRecord.resourceType) {
        navigate(MEDICAL_CONDITIONS);
      }
      return data?.deleteConditionRecord;
    } else if (resourceType === 'Encounter') {
      const { type } = record as EncounterRecord;
      if (type === 'emergency') {
        const { data } = await deleteEmergencyVisit({
          input: { encounter_id: id },
        });
        if (data?.deleteEmergencyVisitRecord.resourceType) {
          navigate(EMERGENCY_VISITS);
        }
        return data?.deleteEmergencyVisitRecord;
      } else if (type === 'hospitalization') {
        const { data } = await deleteHospitalization({
          input: { encounter_id: id },
        });
        if (data?.deleteHospitalizationRecord.resourceType) {
          navigate(HOSPITALIZATIONS);
        }
        return data?.deleteHospitalizationRecord;
      }
    } else if (resourceType === 'Procedure') {
      const { data } = await deleteProcedure({ input: { procedure_id: id } });
      if (data?.deleteProcedureRecord.resourceType) {
        navigate(SURGERIES);
      }
      return data?.deleteProcedureRecord;
    }
  }

  return {
    deleteRecord,
  };
}
